import React, { useState, useEffect, ChangeEvent } from 'react';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { useMediaSlice } from 'app/pages/Media/slice';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { endpoints, mediaBaseUrl } from 'utils/api/endpoints';
import { Star, Trash2 } from 'lucide-react';
import { Button } from 'app/components/ui/button';

function ProductMedia() {
  const form = useFormContext();
  const { useUploadMediaMutation } = useMediaSlice();
  const [
    uploadFile,
    {
      isLoading: isUploadingImage,
      error: fileUploadingError,
      data: fileUploaded,
    },
  ] = useUploadMediaMutation();

  const [media, setMedia] = useState([]);

  // Sync form media with state
  useEffect(() => {
    const formMedia = form.watch('media');
    if (formMedia && Array.isArray(formMedia)) {
      setMedia(formMedia);
    }
  }, [form]);

  const syncMediaWithForm = updatedMedia => {
    setMedia(updatedMedia);
    form.setValue('media', updatedMedia, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleUploadImageFile = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('products', file);

    try {
      await uploadFile(formData);
    } catch (error) {
      toast.error('An unexpected error occurred while uploading the file.');
    }
  };

  const handleDeleteMedia = fileName => {
    const updatedMedia = media.filter(file => file.file_name !== fileName);
    syncMediaWithForm(updatedMedia);
  };

  const handleSetCoverImage = fileName => {
    const updatedMedia = media.map(file => ({
      ...file,
      is_cover: file.file_name === fileName ? 1 : 0,
    }));
    syncMediaWithForm(updatedMedia);
  };

  useEffect(() => {
    if (fileUploadingError) {
      toast.error(fileUploadingError.message || 'File upload failed.');
    }
  }, [fileUploadingError]);

  useEffect(() => {
    if (fileUploaded) {
      const newFile = {
        type: 'image',
        file_name: fileUploaded.response?.products[0],
        is_cover: media.length === 0 ? 1 : 0, // Set first file as cover
      };

      const updatedMedia = [...media, newFile].filter(
        (item, index, self) =>
          index === self.findIndex(t => t.file_name === item.file_name),
      );

      syncMediaWithForm(updatedMedia);
    }
  }, [fileUploaded]);

  return (
    <div className="bg-white  dark:bg-transparent rounded-lg p-4 mb-6">
      <Label htmlFor="file-upload" className="font-medium text-sm mb-2 block">
        Media
      </Label>

      <div className="border-2 border-dotted border-gray-300 rounded-lg flex flex-col items-center justify-center p-6">
        <Label
          htmlFor="file-upload"
          className="bg-gray-100 border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
        >
          {isUploadingImage ? 'Uploading...' : 'Upload New'}
          <Input
            id="file-upload"
            type="file"
            className="hidden"
            accept="image/*,video/*,model/*"
            onChange={handleUploadImageFile}
          />
        </Label>
        <p className="text-sm text-gray-500 mt-2">
          Accepts images, videos, or 3D models
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4 mt-4">
        {media.map((file, index) => (
          <div key={index} className="text-center">
            <div className="flex items-center gap-4 relative border border-gray-200 p-2 rounded-lg">
              <img
                src={`${mediaBaseUrl}/products/${file.file_name}`}
                alt={`Uploaded media ${file.file_name}`}
                className="w-full h-[100px] object-contain"
              />
              <button
                onClick={() => handleSetCoverImage(file.file_name)}
                type="button"
                className="absolute right-2 top-2 z-10 bg-white  dark:bg-transparent rounded-full p-1"
              >
                {file.is_cover ? (
                  <Star className="text-yellow-500" size={16} />
                ) : (
                  <Star className="text-gray-700" size={16} />
                )}
              </button>
            </div>
            <Button
              onClick={() => handleDeleteMedia(file.file_name)}
              className="text-sm text-red-500 underline mt-2 gap-1"
              variant={'ghost'}
              type="button"
            >
              <Trash2 size={16} /> Delete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProductMedia;
